/**
 * Scoperta delle chiavi mostrabili (CF07) — CL07.
 *
 * La regola in una riga: **le chiavi si leggono dallo schema, non dai dati.**
 *
 * Sembra una preferenza di stile e non lo e'. `userdata` contiene anche i campi
 * che l'operatore non puo' vedere: sono dati legittimi, semplicemente non suoi.
 * Derivare da li' l'elenco dei campi mostrabili — o delle voci di un filtro —
 * fa comparire un campo nascosto **appena un task qualsiasi lo porta**, e ne
 * rivela almeno l'esistenza. Basta un task su mille.
 *
 * Lo schema invece e' la proiezione che il server manda, ed e' l'unica che sa
 * cosa l'operatore puo' vedere. Leggere da li' significa che un campo assente
 * dalla proiezione resta assente da ogni superficie, anche se il dato esiste.
 *
 * `undocumentedKeys` non e' un controllo di sicurezza: e' diagnostica. Elenca
 * cio' che i dati portano e lo schema non dichiara, che e' un difetto di
 * configurazione o un residuo — mai qualcosa da mostrare.
 */

import type { LegacySchemaRow } from "./legacyAdapter";

/**
 * Le chiavi che si possono offrire, da uno o piu' schemi.
 *
 * L'unione perche' le attivita' di una persona vengono da modelli diversi, e
 * ogni modello ha i suoi campi. Un nome vuoto non e' una chiave: il contratto
 * gia' lo scarta, qui non deve nemmeno entrare nell'elenco.
 */
export function offerableKeys(
  schemas: readonly (readonly LegacySchemaRow[] | null | undefined)[]
): string[] {
  const keys: string[] = [];

  for (const schema of schemas) {
    for (const field of schema ?? []) {
      const name = field?.name;
      if (typeof name !== "string" || name.trim() === "") continue;
      if (keys.includes(name)) continue;
      keys.push(name);
    }
  }

  return keys;
}

/**
 * Le chiavi presenti nei dati e non dichiarate dallo schema.
 *
 * Si **riportano**, non si mostrano: sono la prova che una sorgente diversa
 * dallo schema e' stata letta, ed e' quello che rende verificabile la regola.
 */
export function undocumentedKeys(
  schemas: readonly (readonly LegacySchemaRow[] | null | undefined)[],
  dataKeys: readonly string[]
): string[] {
  const offerable = new Set(offerableKeys(schemas));
  return Array.from(new Set(dataKeys)).filter((key) => !offerable.has(key));
}

/**
 * Le chiavi di un `userdata`, senza interpretarne i valori.
 *
 * Esiste per la diagnostica, **non** per decidere cosa mostrare: il nome dice
 * cosa fa, e il fatto che serva una funzione dedicata e' deliberato — chi
 * scrivesse `Object.keys(userdata)` in un componente non starebbe usando questa.
 */
export const dataKeysOf = (
  userdata: Record<string, unknown> | null | undefined
): string[] => Object.keys(userdata ?? {});
